import { DatePicker, Form, InputNumber, Select, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePlanDisplayIndex, useVispOptions } from '@/shared/api/reference'
import { usePermissions } from '@/shared/auth/usePermissions'
import { rangeToIso } from '@/shared/lib/dayRange'
import { formatDateTime } from '@/shared/lib/format'
import { shortId } from '@/shared/lib/shortId'
import { FilterDrawer } from '@/shared/ui/FilterDrawer'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import {
  CARD_STATUSES,
  fetchVoucherRegisterPage,
  type CardStatus,
  type VoucherRegisterRow,
} from '@/features/voucher/voucherApi'

type Filters = TableState['filters']

const CARD_STATUS_COLOR: Readonly<Record<CardStatus, string>> = {
  PENDING: 'blue',
  ACTIVE: 'green',
  USED: 'default',
  CANCELLED: 'red',
  EXPIRED: 'orange',
}

const DRAWER_FILTERS = ['planId', 'batchNo', 'serial', 'from'] as const

function single(values: readonly string[] | undefined): string | null {
  return values?.[0] ?? null
}

/** Kartalar reyestri (ADR-056): har karta hisoblangan holati bilan, faollashmaganlari ham */
export function VoucherRegisterPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const { state, patch } = useTableUrlState()
  const planNames = usePlanDisplayIndex(can('plan.read'))
  const canReadVisps = can('network.visp.read')
  const visps = useVispOptions(canReadVisps)

  const status = CARD_STATUSES.find((value) => value === single(state.filters['status'])) ?? null
  const from = single(state.filters['from'])
  const to = single(state.filters['to'])
  const drawerCount = DRAWER_FILTERS.filter((name) => single(state.filters[name]) !== null).length

  const commit = (next: Filters) => patch({ page: 1, filters: next })
  const dash = t('common.dash')
  const column = (key: string) => t(`voucher.register.columns.${key}`)

  const columns: ColumnsType<VoucherRegisterRow> = [
    {
      key: 'card',
      title: column('card'),
      width: 130,
      render: (_value, row) => (
        <bdi dir="ltr">
          <Typography.Text strong>{`#${row.batchNo}-${row.serialNo}`}</Typography.Text>
        </bdi>
      ),
    },
    {
      key: 'code',
      title: column('code'),
      width: 170,
      render: (_value, row) => <Typography.Text code>{row.code}</Typography.Text>,
    },
    { key: 'visp', title: column('visp'), width: 170, ellipsis: true, render: (_value, row) => row.vispName ?? dash },
    {
      key: 'plan',
      title: column('plan'),
      width: 190,
      render: (_value, row) => planNames.get(row.planId) ?? shortId(row.planId),
    },
    {
      key: 'status',
      title: column('status'),
      width: 150,
      render: (_value, row) => (
        <Tooltip
          title={
            row.status === 'CANCELLED' && row.voidReason ? (
              <span dir="auto">{row.voidReason}</span>
            ) : (
              t(`voucher.register.statusHint.${row.status}`)
            )
          }
        >
          <Tag color={CARD_STATUS_COLOR[row.status]}>{t(`voucher.register.status.${row.status}`)}</Tag>
        </Tooltip>
      ),
    },
    {
      key: 'activatedAt',
      title: column('activatedAt'),
      width: 170,
      render: (_value, row) => (row.activatedAt ? formatDateTime(row.activatedAt) : null),
    },
    {
      key: 'expiresAt',
      title: column('expiresAt'),
      width: 170,
      render: (_value, row) => (row.expiresAt ? formatDateTime(row.expiresAt) : dash),
    },
    {
      key: 'subscriber',
      title: column('subscriber'),
      width: 170,
      render: (_value, row) =>
        row.subscriberLogin ? (
          <Link to={`/subscribers?q=${encodeURIComponent(row.subscriberLogin)}`}>{row.subscriberLogin}</Link>
        ) : (
          dash
        ),
    },
    {
      key: 'voidedAt',
      title: column('voidedAt'),
      width: 170,
      render: (_value, row) => (row.voidedAt ? formatDateTime(row.voidedAt) : dash),
    },
  ]

  return (
    <>
      <PageHeader title={t('nav.voucherRegister')} description={t('pageIntro.voucherRegister')} />

      <DataTable<VoucherRegisterRow>
        queryKey={queryKeys.vouchers.register}
        fetchPage={fetchVoucherRegisterPage}
        columns={columns}
        rowKey={(row) => row.voucherId}
        tableId="voucher-register"
        stickyFirstColumn
        scrollX={1500}
        searchable
        searchPlaceholder={t('voucher.register.searchPlaceholder')}
        emptyTitle={t('voucher.register.empty')}
        emptyDescription={t('voucher.register.emptyHint')}
        toolbarExtra={
          <Space wrap>
            <Select<CardStatus | null>
              allowClear
              placeholder={column('status')}
              value={status}
              onChange={(value) => commit({ ...state.filters, status: value ? [value] : [] })}
              style={{ width: 170 }}
              options={CARD_STATUSES.map((value) => ({ value, label: t(`voucher.register.status.${value}`) }))}
            />
            {canReadVisps ? (
              <Select<string | null>
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('voucher.batch.filterVisp')}
                value={single(state.filters['vispId'])}
                onChange={(value) => commit({ ...state.filters, vispId: value ? [value] : [] })}
                style={{ minWidth: 200 }}
                options={visps.map((visp) => ({ value: visp.id, label: `${visp.centerName} / ${visp.name}` }))}
              />
            ) : null}
            <FilterDrawer<Filters>
              value={state.filters}
              resetValue={{ ...state.filters, planId: [], batchNo: [], serial: [], from: [], to: [] }}
              activeCount={drawerCount}
              onApply={commit}
            >
              {(draft, patchDraft) => (
                <>
                  <Form.Item label={column('activatedAt')} extra={t('voucher.register.rangeHint')}>
                    <DatePicker.RangePicker
                      allowClear
                      style={{ width: '100%' }}
                      value={
                        single(draft['from']) && single(draft['to'])
                          ? [dayjs(single(draft['from'])), dayjs(single(draft['to']))]
                          : null
                      }
                      onChange={(value) => {
                        const picked = value?.[0] && value[1] ? rangeToIso([value[0], value[1]]) : null
                        patchDraft({ from: picked ? [picked.from] : [], to: picked ? [picked.to] : [] })
                      }}
                    />
                  </Form.Item>

                  {can('plan.read') ? (
                    <Form.Item label={column('plan')}>
                      <Select<string | null>
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        value={single(draft['planId'])}
                        onChange={(value) => patchDraft({ planId: value ? [value] : [] })}
                        placeholder={t('voucher.activations.planPlaceholder')}
                        style={{ width: '100%' }}
                        options={[...planNames].map(([value, label]) => ({ value, label }))}
                      />
                    </Form.Item>
                  ) : null}

                  <Form.Item label={t('voucher.activations.columns.batchNo')}>
                    <InputNumber<number>
                      min={1}
                      precision={0}
                      style={{ width: '100%' }}
                      value={single(draft['batchNo']) ? Number(single(draft['batchNo'])) : null}
                      onChange={(value) => patchDraft({ batchNo: value ? [String(value)] : [] })}
                      placeholder={t('voucher.activations.batchPlaceholder')}
                    />
                  </Form.Item>

                  <Form.Item label={t('voucher.serial')}>
                    <InputNumber<number>
                      min={1}
                      precision={0}
                      style={{ width: '100%' }}
                      value={single(draft['serial']) ? Number(single(draft['serial'])) : null}
                      onChange={(value) => patchDraft({ serial: value ? [String(value)] : [] })}
                      placeholder={t('voucher.register.serialPlaceholder')}
                    />
                  </Form.Item>
                </>
              )}
            </FilterDrawer>
          </Space>
        }
      />

      {from && to ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('voucher.activations.rangeNote', { from: formatDateTime(from), to: formatDateTime(to) })}
        </Typography.Text>
      ) : null}
    </>
  )
}
